// The place page's routes, as TripGroupMap legs.
//
// useEntryMapLegs is entry-shaped and correctly not reused: this map mixes two
// tables. What makes one routeKey cover both is that every leg is re-serialized
// under the ENTRY key -- the route it carries has already been read with the
// right reader (PlaceUsage's tripPlaceUsage / entryPlaceUsage own that choice)
// and resolved against the gazetteer, so there is nothing left to decide by
// key. routeFromFields takes an object blob, so no JSON round trip is needed.
//
// The route is the one the MATCH was made against, handed over by usePlaceRecords.
// A page that listed by one route and drew another could show a record it has
// no line for; passing the same object makes that impossible rather than
// merely unlikely.

import { useMemo } from 'react';
import { ENTRY_ROUTE_KEY } from '../../fleet-shared/TripRoute.mjs';

// TripGroupMap builds a RouteLayer (a GeoJSON source plus line layers) per leg
// and a WaypointsLayer per marker. A harbour with 300 visits is roughly 900
// maplibre layers, which crawls or locks the tab -- so the DRAWING is capped at
// the most recent N and the card says so. The tables stay complete: the cap is
// a rendering limit, not a claim about the data.
export const MAP_LEG_LIMIT = 50;

export function usePlaceMapLegs(records) {
  return useMemo(() => {
    const drawable = (records ?? []).filter((row) => row.usage.onMap);
    // Newest first out of usePlaceRecords; the map wants oldest first so
    // legColorToken(index) cycles in time order like every other trip map.
    const shown = drawable.slice(0, MAP_LEG_LIMIT).reverse();

    const mapLegs = shown.map((row) => ({
      // Two id spaces on one map. A uuid collision is fantasy, but TripGroupMap
      // builds maplibre layer ids as `leg-${id}`, so prefixing costs nothing
      // and makes onSelectLeg's dispatch a lookup rather than a guess.
      id: `${row.kind}-${row.id}`,
      // Flattened by usePlaceRecords, so this doesn't care which table the
      // anchors were mirrored into.
      from: row.from,
      to: row.to,
      fields: { [ENTRY_ROUTE_KEY]: row.usage.route },
      // Nav info rides on the leg: onSelectLeg hands this very object back.
      kind: row.kind,
      recordId: row.id,
    }));

    return {
      mapLegs,
      anyOnMap: mapLegs.length > 0,
      drawableCount: drawable.length,
      // What the card has to admit to when the cap bites.
      hiddenCount: Math.max(0, drawable.length - mapLegs.length),
      // Records that refer to the place but have no line at all -- a From/To
      // name match on a record predating routes. Counted for the totals line so
      // an empty-looking map is explained rather than mysterious.
      notOnMapCount: (records ?? []).length - drawable.length,
    };
  }, [records]);
}

export default usePlaceMapLegs;
